<script setup lang="ts">
import {useRoute, useRouter} from "vue-router";
import {useStorage} from "@vueuse/core";
import {getPlan, storePlan, updatePlan} from "@/Services/Admin/admin-plans";
import {saving} from "@/Composables/api";
import {Card, InputNumber, InputText, Label, Checkbox, Textarea, ToggleSwitch} from "@/Components/Library";
import {PageHeading} from "@/Components/Global";
import {FormButtons, ResetButton} from "@/Components/Forms";
import {useToast} from "primevue/usetoast";

const currentRoute = useRoute();
const router = useRouter();
const toast = useToast();

const defaultForm = {
    id: null,
    name: null,
    slug: null,
    description: null,
    price: null,
    stripe_price_id: null,
    is_active: true,
    group_limit: 5,
    group_member_limit: 5,
    post_limit: 5,
    ai_tokens_limit: 1000,
    can_ask_detailed_questions: false,
    can_ask_annotation_questions: false,
    can_reuse_questions: false,
    can_export: false,
};

const plan = useStorage("adminPlanForm", {...defaultForm});

if (currentRoute.params.plan) {
    if (plan.value.id !== currentRoute.params.plan) {
        getPlan(currentRoute.params.plan).then(({data}) => {
            Object.assign(plan.value, data);
        });
    }
} else {
    Object.assign(plan.value, defaultForm);
}

const save = async () => {
    if (currentRoute.params.plan) {
        const {data} = await updatePlan(currentRoute.params.plan, plan.value);
        Object.assign(plan.value, data);
    } else {
        const {data} = await storePlan(plan.value);
        router.push({name: "admin.plans.edit", params: {plan: data.id}});
    }

    toast.add({severity: "success", summary: "Success", detail: "Plan saved successfully.", life: 3000});
};

const reset = () => Object.assign(plan.value, defaultForm);
</script>
<template>
    <div class="mx-auto w-full max-w-5xl pb-6">
        <div class="m--container mb-3">
            <PageHeading :title="`${currentRoute.params.plan ? 'Edit' : 'Create'} Plan`">
                <ResetButton @reset="reset" />
            </PageHeading>
        </div>
        <form class="flex flex-col gap-4" @submit.prevent="save">
            <Card>
                <template #title>Basic Information</template>
                <div class="flex flex-col gap-4">
                    <div class="flex flex-col">
                        <Label for="name" class="mb-1" required>Name</Label>
                        <InputText id="name" name="name" v-model="plan.name" required fluid />
                    </div>
                    <div class="flex flex-col">
                        <Label for="slug" class="mb-1" required>Slug</Label>
                        <InputText id="slug" name="slug" v-model="plan.slug" required fluid />
                    </div>
                    <div class="flex flex-col">
                        <Label for="description" class="mb-1">Description</Label>
                        <Textarea id="description" name="description" v-model="plan.description" fluid auto-resize />
                    </div>
                    <div class="flex flex-col">
                        <Label for="price" class="mb-1">Price (USD)</Label>
                        <InputNumber
                            input-id="price"
                            name="price"
                            v-model="plan.price"
                            mode="currency"
                            currency="USD"
                            locale="en-US"
                            :min="0"
                            :max-fraction-digits="2"
                            fluid
                        />
                    </div>
                    <div class="flex items-center gap-2">
                        <ToggleSwitch input-id="is_active" name="is_active" v-model="plan.is_active" />
                        <Label for="is_active">Active</Label>
                    </div>
                </div>
            </Card>
            <Card>
                <template #title>Stripe</template>
                <div class="flex flex-col gap-4">
                    <div class="flex flex-col">
                        <Label for="stripe_price_id" class="mb-1">Price ID</Label>
                        <InputText
                            id="stripe_price_id"
                            name="stripe_price_id"
                            v-model="plan.stripe_price_id"
                            placeholder="price_12345"
                            fluid
                        />
                    </div>
                </div>
            </Card>
            <Card>
                <template #title>Plan Restrictions</template>
                <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <!-- Numeric Limits -->
                    <div>
                        <h4 class="mb-2 font-medium">Limits</h4>
                        <div class="space-y-8">
                            <div class="flex flex-col" v-auto-animate>
                                <div class="mb-2 flex items-end">
                                    <Label for="group_limit">Group Limit</Label>
                                    <div class="ml-auto flex items-center">
                                        <label class="mr-1 text-sm">Unlimited</label>
                                        <ToggleSwitch :true-value="-1" v-model="plan.group_limit" />
                                    </div>
                                </div>
                                <InputNumber
                                    v-if="plan.group_limit > -1"
                                    inputId="group_limit"
                                    buttonLayout="horizontal"
                                    showButtons
                                    v-model="plan.group_limit"
                                    :min="-1"
                                    fluid
                                />
                                <hr v-else />
                            </div>
                            <div class="flex flex-col" v-auto-animate>
                                <div class="mb-2 flex items-end">
                                    <Label for="group_member_limit">Group Member Limit</Label>
                                    <div class="ml-auto flex items-center">
                                        <label class="mr-1 text-sm">Unlimited</label>
                                        <ToggleSwitch :true-value="-1" v-model="plan.group_member_limit" />
                                    </div>
                                </div>
                                <InputNumber
                                    v-if="plan.group_member_limit > -1"
                                    inputId="group_member_limit"
                                    buttonLayout="horizontal"
                                    showButtons
                                    v-model="plan.group_member_limit"
                                    :min="-1"
                                    fluid
                                />
                                <hr v-else />
                            </div>
                            <div class="flex flex-col" v-auto-animate>
                                <div class="mb-2 flex items-end">
                                    <Label for="post_limit">Post Limit</Label>
                                    <div class="ml-auto flex items-center">
                                        <label class="mr-1 text-sm">Unlimited</label>
                                        <ToggleSwitch :true-value="-1" v-model="plan.post_limit" />
                                    </div>
                                </div>
                                <InputNumber
                                    v-if="plan.post_limit > -1"
                                    inputId="post_limit"
                                    buttonLayout="horizontal"
                                    showButtons
                                    v-model="plan.post_limit"
                                    :min="-1"
                                    fluid
                                />
                                <hr v-else />
                            </div>
                            <div class="flex flex-col" v-auto-animate>
                                <div class="mb-2 flex items-end">
                                    <Label for="ai_tokens_limit">AI Tokens Limit</Label>
                                    <div class="ml-auto flex items-center">
                                        <label class="mr-1 text-sm">Unlimited</label>
                                        <ToggleSwitch :true-value="-1" v-model="plan.ai_tokens_limit" />
                                    </div>
                                </div>
                                <InputNumber
                                    v-if="plan.ai_tokens_limit > -1"
                                    inputId="ai_tokens_limit"
                                    buttonLayout="horizontal"
                                    showButtons
                                    v-model="plan.ai_tokens_limit"
                                    :min="-1"
                                    fluid
                                />
                            </div>
                        </div>
                    </div>
                    <!-- Feature Permissions -->
                    <div class="space-y-4">
                        <h4 class="font-medium">Permissions</h4>
                        <div class="flex items-center gap-2">
                            <Checkbox
                                binary
                                inputId="can_ask_detailed_questions"
                                v-model="plan.can_ask_detailed_questions"
                            />
                            <Label for="can_ask_detailed_questions">Ask Detailed Questions</Label>
                        </div>
                        <div class="flex items-center gap-2">
                            <Checkbox
                                binary
                                inputId="can_ask_annotation_questions"
                                v-model="plan.can_ask_annotation_questions"
                            />
                            <Label for="can_ask_annotation_questions">Allow Annotation Questions</Label>
                        </div>
                        <div class="flex items-center gap-2">
                            <Checkbox binary inputId="can_reuse_questions" v-model="plan.can_reuse_questions" />
                            <Label for="can_reuse_questions">Reuse Questions / Stimuli</Label>
                        </div>
                        <div class="flex items-center gap-2">
                            <Checkbox binary inputId="can_export" v-model="plan.can_export" />
                            <Label for="can_export">Export Data</Label>
                        </div>
                    </div>
                </div>
            </Card>
            <FormButtons :loading="!!saving" :show-cancel="false" />
        </form>
    </div>
</template>
<style scoped></style>
